import { type MouseEvent, useMemo } from 'react';
import { css } from '@emotion/react';
import { ArrowSquareOut } from '@phosphor-icons/react';
import { toTitleCase } from 'common/commonUtil';
import { EventType } from 'common/EventConstants';
import { StatusItemState } from 'common/status/StatusDocument';
import { useApi } from 'client/lib/trpc';
import { useDeploymentClientState } from 'client/state/DeploymentClientState';
import { useDeploymentStatusTree } from 'client/state/useDeploymentStatusTree';
import { useDeploymentWarning } from 'client/pages/deployment/useDeploymentWarning';
import { Button } from 'client/components/Button';
import { DropdownMenu, type DropdownMenuItemProps } from 'client/components/DropdownMenu';
import { ComponentIcon } from 'client/sections/components/ComponentIcon';
import { useViewAppBlocker } from './useViewAppBlocker';
import { ViewAppMenuItem } from './ViewAppMenuItem';

export const ViewAppButton = () => {
  const api = useApi();
  const deploymentID = useDeploymentClientState((state) => state.document?.id);
  const statusTree = useDeploymentStatusTree(deploymentID);
  const componentValues = useDeploymentClientState((state) => state.document?.componentsWithValues || []);

  const hosts = useMemo(
    () =>
      componentValues.flatMap((component) =>
        component.hosts.map((host) => ({
          ...host,
          stackComponentID: component.stackComponentID,
          componentName: component.name,
          packageUrl: component.packageUrl || '',
        }))
      ),
    [componentValues]
  );

  const defaultHost =
    hosts.length === 1 ? `https://${hosts[0].customDomain || hosts[0].tweakedDomain || hosts[0].defaultDomain || ''}` : null;

  const { menuItems } = useViewAppBlocker({ deploymentID, href: defaultHost });

  const stackComponentIDs = hosts.map((host) => host.stackComponentID);

  const hostsContainers = stackComponentIDs.map((stackComponentID) =>
    (statusTree?.containers.items || []).filter((item) => item.stackComponentID === stackComponentID)
  );

  const readyHostsContainers = hostsContainers.flatMap((containers) =>
    containers.some((item) => item.itemStateID !== StatusItemState.GREEN) ? [] : containers
  );

  const { isWarningDisplayed, loadingToReadyState } = useDeploymentWarning({ containers: readyHostsContainers });

  const handleOpen = () => {
    if (deploymentID) {
      api.status.eventCreate.mutate({
        eventTypeID: EventType.DEPLOYMENT_HOST_OPEN,
        deploymentID,
        host: 'auto',
        reason: 'open',
      });
    }
  };

  const handlePreventOpen = (event?: MouseEvent<HTMLButtonElement>) => {
    if (deploymentID && isWarningDisplayed) {
      if (event) event.preventDefault();
      api.status.eventCreate.mutate({
        eventTypeID: EventType.DEPLOYMENT_HOST_OPEN_INACTIVE,
        deploymentID,
        host: 'auto',
        reason: 'starting',
      });
    }
  };

  const hostDropdownMenuItems = useMemo(
    () =>
      hosts.map((host) => {
        const props: DropdownMenuItemProps = {
          id: `${host.stackComponentID}-${host.identifier}`,
          title: `View ${host.componentName} ${toTitleCase(host.identifier)}`,
          subtitle: (
            <span css={subtitleStyle}>
              <ArrowSquareOut /> Opens in a new tab
            </span>
          ),
          icon: <ComponentIcon type="component" url={host.packageUrl} hideType />,
          href: `https://${host.customDomain || host.tweakedDomain || host.defaultDomain || ''}`,
          external: true,
          disabled: (statusTree?.containers.items || [])
            .filter((item) => item.stackComponentID === host.stackComponentID)
            .some((item) => item.itemStateID !== StatusItemState.GREEN),
        };

        return <ViewAppMenuItem {...props} stackComponentID={host.stackComponentID} />;
      }),
    [hosts, statusTree?.containers.items]
  );

  const isHttpHostEnabled = !!hosts?.length;

  if (!isHttpHostEnabled) return null;

  const button = (
    <Button
      href={defaultHost || undefined}
      external
      pulsing={loadingToReadyState === 'loading'}
      highlight={loadingToReadyState === 'ready'}
      icon={<ArrowSquareOut />}
      onClick={isWarningDisplayed ? handlePreventOpen : handleOpen}
      primary
    >
      View App
    </Button>
  );

  if (hosts.length > 1) {
    return (
      <DropdownMenu items={hostDropdownMenuItems}>
        <button.type {...button.props} onClick={handlePreventOpen} />
      </DropdownMenu>
    );
  } else if (isWarningDisplayed) {
    return <DropdownMenu items={menuItems}>{button}</DropdownMenu>;
  }

  return button;
};

const subtitleStyle = css`
  display: flex;
  align-items: center;
  gap: 4px;
`;
